"use client"
 
import * as React from "react"
import { endOfMonth, endOfWeek, endOfYear, format, isValid, startOfMonth, startOfWeek, startOfYear, subDays, subMonths, subWeeks, subYears } from "date-fns"
import { Calendar as CalendarIcon, ChevronDown } from "lucide-react"
import { DateRange } from "react-day-picker"
 
import { cn } from "../lib/utils"
import { Button } from "./button"
import { Calendar } from "./calendar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./popover"
import { nlBE, enUS, fr } from "date-fns/locale"
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "./select"
import DateInput from "./date-input"

interface DatePickerWithRangePresetsProps extends React.HTMLAttributes<HTMLDivElement> {
  placeholder?: string;
  onDateChange?: (value:DateRange | undefined) => void;
  locale?: 'nlBE' | 'enUS' | 'fr';
  value?: DateRange | undefined
}

const DatePickerWithRangePresets : React.FC<DatePickerWithRangePresetsProps> = ({className, placeholder="Pick a date", onDateChange, locale='nlBE', value}) => {
  const [date, setDate] = React.useState<DateRange | undefined>({
    from: undefined,
    to: undefined,
  })

  React.useEffect(()=>{
    setDate(value)
  }, [value])

  function handlePickPreset(preset:string) {
    switch (preset) {
      case 'td':
        handleDateChange({from:new Date, to:new Date})
        break;
      case 'yd':
        handleDateChange({from:subDays(new Date, 1), to:subDays(new Date, 1)})
        break;
      case 'tw':
        handleDateChange({from:startOfWeek(new Date, {weekStartsOn:1}), to:endOfWeek(new Date, {weekStartsOn:1})})
        break;
      case 'dw':
        handleDateChange({from:subWeeks(new Date, 1), to:new Date})
        break;
      case 'pw':
        handleDateChange({from:startOfWeek(subWeeks(new Date, 1), {weekStartsOn:1}), to:endOfWeek(subWeeks(new Date, 1), {weekStartsOn:1})})
        break;
      case 'tm':
        handleDateChange({from:startOfMonth(new Date), to:endOfMonth(new Date)})
        break;
      case 'pm':
        handleDateChange({from:startOfMonth(subMonths(new Date,1)), to:endOfMonth(subMonths(new Date, 1))})
        break;
      case 'dm':
        handleDateChange({from:subMonths(new Date,1), to:new Date})
        break;
      case 'ty':
        handleDateChange({from:startOfYear(new Date), to:endOfYear(new Date)})
        break;
      case 'dy':
        handleDateChange({from:subYears(new Date,1), to:new Date})
        break;
      case 'py':
        handleDateChange({from:startOfYear(subYears(new Date,1)), to:endOfYear(subYears(new Date,1))})
        break;
      default:
        break;
    }
  }

  function handleDateChange(range:DateRange|undefined) {
    setDate(range); 
    onDateChange && onDateChange(range)
  }

  return (
    <div className={cn("grid gap-2", className)}>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            id="date"
            variant={"outline"}
            className={cn(
              "w-full justify-start text-left font-normal",
              !date && "text-muted-foreground"
            )}
          >
            <CalendarIcon className="w-4 h-4 mr-2" />
            {date?.from ? (
              date.to ? (
                <>
                  {format(date.from, "LLL dd, y")} -{" "}
                  {format(date.to, "LLL dd, y")}
                </>
              ) : (
                format(date.from, "LLL dd, y")
              )
            ) : (
              <span>{placeholder}</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <div className="flex px-3 pt-2 space-x-1">
            <button onClick={()=>{handlePickPreset("td")}} className="bg-slate-100 text-primary font-medium text-xs rounded-full px-1.5 py-1 capitalize gap-1 flex items-center border border-slate-100 whitespace-nowrap h-fit">Vandaag</button>
            <button onClick={()=>{handlePickPreset("yd")}} className="bg-slate-100 text-primary font-medium text-xs rounded-full px-1.5 py-1 capitalize gap-1 flex items-center border border-slate-100 whitespace-nowrap h-fit">Gisteren</button>
            <button onClick={()=>{handlePickPreset("tw")}} className="bg-slate-100 text-primary font-medium text-xs rounded-full px-1.5 py-1 capitalize gap-1 flex items-center border border-slate-100 whitespace-nowrap h-fit">Deze week</button>
            <Select onValueChange={(value)=>{handlePickPreset(value)}}>
              <SelectTrigger className="bg-slate-100 text-primary font-medium text-xs rounded-full p-1 px-1.5 h-fit capitalize gap-1 flex items-center border border-slate-100 w-fit">
                <SelectValue placeholder={<p>Meer</p>}/>
              </SelectTrigger>
              <SelectContent className="px-1 space-y-1 text-sm">
                <SelectGroup>
                  <SelectItem value="dw" className="p-1 pl-6 cursor-pointer"><button className="w-full p-1 text-left rounded-sm hover:bg-slate-100">Dynamische week</button></SelectItem>
                  <SelectItem value="pw" className="p-1 pl-6 cursor-pointer"><button className="w-full p-1 text-left rounded-sm hover:bg-slate-100">Vorige week</button></SelectItem>
                  <SelectItem value="tm" className="p-1 pl-6 cursor-pointer"><button className="w-full p-1 text-left rounded-sm hover:bg-slate-100">Deze maand</button></SelectItem>
                  <SelectItem value="pm" className="p-1 pl-6 cursor-pointer"><button className="w-full p-1 text-left rounded-sm hover:bg-slate-100">Vorige maand</button></SelectItem>
                  <SelectItem value="dm" className="p-1 pl-6 cursor-pointer"><button className="w-full p-1 text-left rounded-sm hover:bg-slate-100">Dynamische maand</button></SelectItem>
                  <SelectItem value="ty" className="p-1 pl-6 cursor-pointer"><button className="w-full p-1 text-left rounded-sm hover:bg-slate-100">Dit jaar</button></SelectItem>
                  <SelectItem value="py" className="p-1 pl-6 cursor-pointer"><button className="w-full p-1 text-left rounded-sm hover:bg-slate-100">Vorig jaar</button></SelectItem>
                  <SelectItem value="dy" className="p-1 pl-6 cursor-pointer"><button className="w-full p-1 text-left rounded-sm hover:bg-slate-100">Dynamisch jaar</button></SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
          <Calendar
            initialFocus
            mode="range"
            defaultMonth={date?.from}
            selected={date}
            onSelect={(range) => { handleDateChange(range) }}
            numberOfMonths={2}
            locale={locale === 'nlBE' ? nlBE : locale === 'fr' ? fr : locale === 'enUS' ? enUS : nlBE}
          />
        </PopoverContent>
      </Popover>
    </div>
  )
}

const DatePickerWithRangePresetsInput : React.FC<DatePickerWithRangePresetsProps> = ({className, placeholder="Pick a date", onDateChange, locale='nlBE', value}) => {
  const [date, setDate] = React.useState<DateRange | undefined>({
    from: undefined,
    to: undefined,
  })

  React.useEffect(()=>{
    if(value){
      setDate(value)
    }
  }, [])

  function handlePickPreset(preset:string) {
    switch (preset) {
      case 'td':
        handleDateChange({from:new Date, to:new Date})
        break;
      case 'yd':
        handleDateChange({from:subDays(new Date, 1), to:subDays(new Date, 1)})
        break;
      case 'tw':
        handleDateChange({from:startOfWeek(new Date, {weekStartsOn:1}), to:endOfWeek(new Date, {weekStartsOn:1})})
        break;
      case 'dw':
        handleDateChange({from:subWeeks(new Date, 1), to:new Date})
        break;
      case 'pw':
        handleDateChange({from:startOfWeek(subWeeks(new Date, 1), {weekStartsOn:1}), to:endOfWeek(subWeeks(new Date, 1), {weekStartsOn:1})})
        break;
      case 'tm':
        handleDateChange({from:startOfMonth(new Date), to:endOfMonth(new Date)})
        break;
      case 'pm':
        handleDateChange({from:startOfMonth(subMonths(new Date,1)), to:endOfMonth(subMonths(new Date, 1))})
        break;
      case 'dm':
        handleDateChange({from:subMonths(new Date,1), to:new Date})
        break;
      case 'ty':
        handleDateChange({from:startOfYear(new Date), to:endOfYear(new Date)})
        break;
      case 'dy':
        handleDateChange({from:subYears(new Date,1), to:new Date})
        break;
      case 'py':
        handleDateChange({from:startOfYear(subYears(new Date,1)), to:endOfYear(subYears(new Date,1))})
        break;
      case 'at':
        handleDateChange({from:subYears(new Date,100), to:new Date})
        break;
      default:
        break;
    }
  }

  function handleDateChange(range:DateRange|undefined) {
    setDate(range); 
  }

  React.useEffect(()=>{
    onDateChange && onDateChange(date)
  }, [date])

  return (
    <div className={cn("w-full",className)}>
      <Popover>
        <div className='flex items-center w-full h-10 px-3 py-2 text-sm border rounded-md border-input bg-background'>
          <DateInput 
            hideIcon 
            variant='inline' 
            onValueChange={(d)=>{ setDate({...date, from:d ?? undefined}) }} 
            className='focus:outline-none w-[84px]' 
            value={date?.from}
          />
          <span className='ml-1 mr-1.5'>-</span>
          <DateInput 
            hideIcon 
            variant='inline' 
            onValueChange={(d)=>{ setDate({from:date?.from, to:d ?? undefined}) }} 
            className='w-full focus:outline-none' 
            value={date?.to}
          />
          <PopoverTrigger asChild>
            <button className='h-8 w-8 min-w-[32px] flex justify-center items-center hover:bg-accent rounded-full translate-x-2 text-neutral-500'><CalendarIcon className='w-4 h-4'/></button>
          </PopoverTrigger>
        </div>
        <PopoverContent className="w-auto p-0" align="start">
          <div className="flex px-3 pt-2 space-x-1">
            <button onClick={()=>{handlePickPreset("td")}} className="bg-slate-100 text-primary font-medium text-xs rounded-full px-1.5 py-1 capitalize gap-1 flex items-center border border-slate-100 whitespace-nowrap h-fit">Vandaag</button>
            <button onClick={()=>{handlePickPreset("yd")}} className="bg-slate-100 text-primary font-medium text-xs rounded-full px-1.5 py-1 capitalize gap-1 flex items-center border border-slate-100 whitespace-nowrap h-fit">Gisteren</button>
            <button onClick={()=>{handlePickPreset("tw")}} className="bg-slate-100 text-primary font-medium text-xs rounded-full px-1.5 py-1 capitalize gap-1 flex items-center border border-slate-100 whitespace-nowrap h-fit">Deze week</button>
            <Select onValueChange={(value)=>{handlePickPreset(value)}}>
              <SelectTrigger className="bg-slate-100 text-primary font-medium text-xs rounded-full px-1.5 h-fit py-0 capitalize gap-1 flex items-center border border-slate-100 w-fit">
                <SelectValue placeholder={<p className="p-1">Meer</p>}/>
              </SelectTrigger>
              <SelectContent className="px-1 space-y-1 text-sm">
                <SelectGroup>
                  <SelectItem value="dw" className="p-0 pl-6"><button className="w-full p-1 text-left capitalize rounded-sm hover:bg-slate-100">Dynamische week</button></SelectItem>
                  <SelectItem value="pw" className="p-0 pl-6"><button className="w-full p-1 text-left capitalize rounded-sm hover:bg-slate-100">Vorige week</button></SelectItem>
                  <SelectItem value="tm" className="p-0 pl-6"><button className="w-full p-1 text-left capitalize rounded-sm hover:bg-slate-100">Deze maand</button></SelectItem>
                  <SelectItem value="pm" className="p-0 pl-6"><button className="w-full p-1 text-left capitalize rounded-sm hover:bg-slate-100">Vorige maand</button></SelectItem>
                  <SelectItem value="dm" className="p-0 pl-6"><button className="w-full p-1 text-left capitalize rounded-sm hover:bg-slate-100">Dynamische maand</button></SelectItem>
                  <SelectItem value="ty" className="p-0 pl-6"><button className="w-full p-1 text-left capitalize rounded-sm hover:bg-slate-100">Dit jaar</button></SelectItem>
                  <SelectItem value="py" className="p-0 pl-6"><button className="w-full p-1 text-left capitalize rounded-sm hover:bg-slate-100">Vorig jaar</button></SelectItem>
                  <SelectItem value="dy" className="p-0 pl-6"><button className="w-full p-1 text-left capitalize rounded-sm hover:bg-slate-100">Dynamisch jaar</button></SelectItem>
                  <SelectItem value="at" className="p-0 pl-6"><button className="w-full p-1 text-left capitalize rounded-sm hover:bg-slate-100">Altijd</button></SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
          <Calendar
            initialFocus
            mode="range"
            defaultMonth={date?.from}
            selected={date}
            onSelect={(range) => { handleDateChange(range) }}
            numberOfMonths={2}
            locale={locale === 'nlBE' ? nlBE : locale === 'fr' ? fr : locale === 'enUS' ? enUS : nlBE}
          />
        </PopoverContent>
      </Popover>
    </div>
  )
}


export { DatePickerWithRangePresets, DatePickerWithRangePresetsInput };